import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SubsectionPanel from '../../common/SubsectionPanel/SubsectionPanel';
import InfoPanel from '../../common/InfoPanel/InfoPanel';
import TextInput from '../../widgets/TextInput';
import { CompanyInfoRestDataProvider } from '../../../data/CompanyInfoRestDataProvider';
import { CompanyInfo } from '../../../models/CompanyInfo';
import { BinaryFileRestDataProvider } from '../../../data/BinaryFileRestDataProvider';
import PopupDialog from '../../common/PopupDialog/PopupDialog';
import { FaSave, FaUpload, FaTrash, FaImage } from 'react-icons/fa';
import './CompanyInfoTab.scss';
import { useTranslation } from 'react-i18next';

const emptyInfo: CompanyInfo = {
  id: '00000000-0000-0000-0000-000000000001',
  companyName: '',
  kvkNumber: '',
  btwId: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  zipCode: '',
  provinceOrState: '',
  country: '',
  phoneNumber: '',
  emailAddress: '',
  website: '',
  bankAccountNumber: '',
  logoFileId: null,
};

const CompanyInfoTab: React.FC = () => {
  const { t } = useTranslation(['common', 'screens']);
  const [data, setData] = useState<CompanyInfo>(emptyInfo);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedOk, setSavedOk] = useState(false);

  const provider = useMemo(() => new CompanyInfoRestDataProvider(), []);
  const binaryFileProvider = useMemo(() => new BinaryFileRestDataProvider(), []);
  const logoFileInputRef = useRef<HTMLInputElement>(null);

  const [logoUploadOpen, setLogoUploadOpen] = useState(false);
  const [logoRemoveOpen, setLogoRemoveOpen] = useState(false);
  const [selectedLogoFile, setSelectedLogoFile] = useState<File | null>(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);

  const loadInfo = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await provider.getInfo());
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [provider]);

  useEffect(() => { loadInfo(); }, [loadInfo]);

  useEffect(() => {
    if (!data.logoFileId) { setLogoPreviewUrl(null); return; }
    let objectUrl: string | null = null;
    binaryFileProvider.download(data.logoFileId).then(blob => {
      objectUrl = URL.createObjectURL(blob);
      setLogoPreviewUrl(objectUrl);
    }).catch(() => setLogoPreviewUrl(null));
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [data.logoFileId, binaryFileProvider]);

  const set = <K extends keyof CompanyInfo>(field: K, value: CompanyInfo[K]) =>
    setData(prev => ({ ...prev, [field]: value }));

  const handleLogoUpload = async () => {
    if (!selectedLogoFile) return;
    setLogoUploading(true);
    setError(null);
    try {
      const uploaded = await binaryFileProvider.upload(selectedLogoFile);
      const newData = { ...data, logoFileId: uploaded.id };
      await provider.saveInfo(newData);
      setData(newData);
      setLogoUploadOpen(false);
      setSelectedLogoFile(null);
      if (logoFileInputRef.current) logoFileInputRef.current.value = '';
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLogoUploading(false);
    }
  };

  const handleLogoRemove = async () => {
    if (!data.logoFileId) return;
    setError(null);
    try {
      await binaryFileProvider.delete(data.logoFileId);
      const newData = { ...data, logoFileId: null };
      await provider.saveInfo(newData);
      setData(newData);
      setLogoRemoveOpen(false);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSavedOk(false);
    setError(null);
    try {
      await provider.saveInfo(data);
      await loadInfo();
      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 3000);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="company-info-tab">
      <SubsectionPanel title={t('screens:companyInfo.title')}>
        <InfoPanel header="About this section">
          Configure your own company details here. This information is used on invoices,
          reports, and other outgoing documents to identify your business.
        </InfoPanel>

        {error && <div className="company-info-tab__error">{error}</div>}
        {loading && <div className="company-info-tab__loading">{t('common:loading')}</div>}

        {!loading && (
          <form onSubmit={handleSave}>

            {/* ── Section: Company Identity ── */}
            <div className="company-info-tab__section">
              <div className="company-info-tab__section-title">{t('screens:companyInfo.sections.identity')}</div>
              <div className="company-info-tab__row">
                <div className="company-info-tab__col company-info-tab__col--wide">
                  <TextInput
                    label={t('screens:companyInfo.fields.companyName')}
                    value={data.companyName ?? ''}
                    required
                    onChange={v => set('companyName', v)}
                  />
                </div>
                <div className="company-info-tab__col">
                  <TextInput
                    label={t('screens:companyInfo.fields.kvkNumber')}
                    value={data.kvkNumber ?? ''}
                    onChange={v => set('kvkNumber', v)}
                  />
                </div>
                <div className="company-info-tab__col">
                  <TextInput
                    label={t('screens:companyInfo.fields.btwVatId')}
                    value={data.btwId ?? ''}
                    onChange={v => set('btwId', v)}
                  />
                </div>
              </div>
            </div>

            {/* ── Section: Address ── */}
            <div className="company-info-tab__section">
              <div className="company-info-tab__section-title">{t('screens:companyInfo.sections.address')}</div>
              <div className="company-info-tab__row">
                <div className="company-info-tab__col company-info-tab__col--wide">
                  <TextInput
                    label={t('screens:companyInfo.fields.addressLine1')}
                    value={data.addressLine1 ?? ''}
                    onChange={v => set('addressLine1', v)}
                  />
                </div>
                <div className="company-info-tab__col company-info-tab__col--wide">
                  <TextInput
                    label={t('screens:companyInfo.fields.addressLine2')}
                    value={data.addressLine2 ?? ''}
                    onChange={v => set('addressLine2', v)}
                  />
                </div>
              </div>
              <div className="company-info-tab__row">
                <div className="company-info-tab__col company-info-tab__col--wide">
                  <TextInput
                    label={t('screens:companyInfo.fields.city')}
                    value={data.city ?? ''}
                    onChange={v => set('city', v)}
                  />
                </div>
                <div className="company-info-tab__col">
                  <TextInput
                    label={t('screens:companyInfo.fields.zipCode')}
                    value={data.zipCode ?? ''}
                    onChange={v => set('zipCode', v)}
                  />
                </div>
              </div>
              <div className="company-info-tab__row">
                <div className="company-info-tab__col">
                  <TextInput
                    label={t('screens:companyInfo.fields.provinceState')}
                    value={data.provinceOrState ?? ''}
                    onChange={v => set('provinceOrState', v)}
                  />
                </div>
                <div className="company-info-tab__col">
                  <TextInput
                    label={t('screens:companyInfo.fields.country')}
                    value={data.country ?? ''}
                    onChange={v => set('country', v)}
                  />
                </div>
              </div>
            </div>

            {/* ── Section: Contact ── */}
            <div className="company-info-tab__section">
              <div className="company-info-tab__section-title">{t('screens:companyInfo.sections.contact')}</div>
              <div className="company-info-tab__row">
                <div className="company-info-tab__col">
                  <TextInput
                    label={t('screens:companyInfo.fields.phoneNumber')}
                    value={data.phoneNumber ?? ''}
                    onChange={v => set('phoneNumber', v)}
                  />
                </div>
                <div className="company-info-tab__col company-info-tab__col--wide">
                  <TextInput
                    label={t('screens:companyInfo.fields.emailAddress')}
                    value={data.emailAddress ?? ''}
                    onChange={v => set('emailAddress', v)}
                  />
                </div>
                <div className="company-info-tab__col company-info-tab__col--wide">
                  <TextInput
                    label={t('screens:companyInfo.fields.website')}
                    value={data.website ?? ''}
                    onChange={v => set('website', v)}
                  />
                </div>
              </div>
            </div>

            {/* ── Section: Financial ── */}
            <div className="company-info-tab__section">
              <div className="company-info-tab__section-title">{t('screens:companyInfo.sections.financial')}</div>
              <div className="company-info-tab__row">
                <div className="company-info-tab__col company-info-tab__col--wide">
                  <TextInput
                    label={t('screens:companyInfo.fields.bankAccountNumber')}
                    value={data.bankAccountNumber ?? ''}
                    onChange={v => set('bankAccountNumber', v)}
                  />
                </div>
              </div>
            </div>

            {/* ── Section: Logo ── */}
            <div className="company-info-tab__section">
              <div className="company-info-tab__section-title">{t('screens:companyInfo.sections.logo')}</div>
              <div className="company-info-tab__logo-area">
                <div className="company-info-tab__logo-preview">
                  {logoPreviewUrl ? (
                    <img
                      src={logoPreviewUrl}
                      alt="Company Logo"
                      className="company-info-tab__logo-img"
                    />
                  ) : (
                    <div className="company-info-tab__logo-placeholder">
                      <FaImage className="company-info-tab__logo-icon" />
                      <span>{t('screens:companyInfo.logo.noLogoUploaded')}</span>
                    </div>
                  )}
                </div>
                <div className="company-info-tab__logo-actions">
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => setLogoUploadOpen(true)}
                  >
                    <FaUpload style={{ marginRight: 5 }} />
                    {data.logoFileId ? t('screens:companyInfo.logo.changeLogo') : t('screens:companyInfo.logo.uploadLogo')}
                  </button>
                  {data.logoFileId && (
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => setLogoRemoveOpen(true)}
                    >
                      <FaTrash style={{ marginRight: 5 }} />{t('screens:companyInfo.logo.removeLogo')}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* ── Footer ── */}
            <div className="company-info-tab__footer">
              {savedOk && (
                <span className="company-info-tab__saved-badge">
                  {t('screens:companyInfo.savedSuccessfully')}
                </span>
              )}
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
              >
                <FaSave style={{ marginRight: 6 }} />
                {saving ? t('common:saving') : t('common:save')}
              </button>
            </div>

          </form>
        )}
      </SubsectionPanel>

      {/* ── Logo upload popup ── */}
      <PopupDialog
        open={logoUploadOpen}
        title={t('screens:companyInfo.logo.uploadDialog')}
        onClose={() => { setLogoUploadOpen(false); setSelectedLogoFile(null); if (logoFileInputRef.current) logoFileInputRef.current.value = ''; }}
        actions={
          <>
            <button
              type="button"
              className="btn btn-primary btn-sm me-2"
              disabled={!selectedLogoFile || logoUploading}
              onClick={handleLogoUpload}
            >
              <FaUpload style={{ marginRight: 5 }} />
              {logoUploading ? t('screens:companyInfo.logo.uploading') : t('common:upload')}
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => { setLogoUploadOpen(false); setSelectedLogoFile(null); if (logoFileInputRef.current) logoFileInputRef.current.value = ''; }}
            >
              {t('common:cancel')}
            </button>
          </>
        }
      >
        <div style={{ minWidth: 340 }}>
          <label className="form-label">{t('screens:companyInfo.logo.selectImageFile')}</label>
          <input
            ref={logoFileInputRef}
            type="file"
            className="form-control"
            accept="image/*"
            onChange={e => setSelectedLogoFile(e.target.files?.[0] ?? null)}
          />
          {selectedLogoFile && (
            <p className="mt-2" style={{ fontSize: '0.85rem', color: '#495057' }}>
              {selectedLogoFile.name}
            </p>
          )}
        </div>
      </PopupDialog>

      {/* ── Logo remove confirm popup ── */}
      <PopupDialog
        open={logoRemoveOpen}
        title={t('screens:companyInfo.logo.removeDialog')}
        onClose={() => setLogoRemoveOpen(false)}
        actions={
          <>
            <button
              type="button"
              className="btn btn-danger btn-sm me-2"
              onClick={handleLogoRemove}
            >
              <FaTrash style={{ marginRight: 5 }} />{t('common:remove')}
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setLogoRemoveOpen(false)}
            >
              {t('common:cancel')}
            </button>
          </>
        }
      >
        <p>{t('screens:companyInfo.logo.confirmRemove')}</p>
      </PopupDialog>
    </div>
  );
};

export default CompanyInfoTab;
